
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { PrayerImage } from '../../../lib/supabase';

export default function WednesdayPrayer() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [images, setImages] = useState<PrayerImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          if (sectionRef.current) observer.unobserve(sectionRef.current);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (observer && sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const { data: latestWeek } = await supabase
        .from('prayer_images')
        .select('week_date')
        .order('week_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (latestWeek) {
        const { data, error } = await supabase
          .from('prayer_images')
          .select('*')
          .eq('week_date', latestWeek.week_date)
          .order('display_order', { ascending: true });

        if (error) throw error;
        setImages(data || []);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLatestDate = () => {
    if (images.length === 0) return '';
    return new Date(images[0].week_date).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const openModal = (index: number) => {
    setModalIndex(index);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = '';
  };

  const modalNext = () => {
    setModalIndex((prev) => (prev + 1) % images.length);
  };

  const modalPrev = () => {
    setModalIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <section
      ref={sectionRef}
      id="wednesday-prayer"
      className={`
        py-12 sm:py-16 md:py-24 
        bg-gradient-to-b from-[#2F4F4F] to-[#1a3333] 
        transition-opacity duration-1000 
        ${isVisible ? 'opacity-100' : 'opacity-0'}
      `}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6">
            <i className="ri-time-line text-base sm:text-lg md:text-xl text-amber-300"></i>
            <span className="text-white/90 font-medium text-xs sm:text-sm md:text-base">
              매주 수요일 오후 9시 10분
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">
            수요910 기도
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            매주 수요일 밤 9시 10분, 선교지와 선교사님들을 위해
            <br className="hidden sm:block" />
            함께 기도하는 시간입니다.
          </p>
        </div>

        {/* Prayer Content Card */}
        <div
          className={`
            max-w-5xl mx-auto 
            ${isVisible ? 'animate-fade-in' : 'opacity-0'}
          `}
        >
          <div className="bg-white rounded-2xl sm:rounded-[24px] shadow-2xl overflow-hidden">
            {/* Card Header */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl flex items-center justify-center">
                    <i className="ri-hands-clapping-line text-xl sm:text-2xl md:text-3xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">이번 주 기도제목</h3>
                    <p className="text-white/80 text-xs sm:text-sm">Weekly Prayer Request</p>
                  </div>
                </div>
                {images.length > 0 && (
                  <div className="hidden sm:block text-right">
                    <div className="text-white/80 text-xs sm:text-sm">{getLatestDate()}</div>
                    <div className="text-white text-xs mt-1">{images.length}장의 기도제목</div>
                  </div>
                )}
              </div>
            </div>

            {/* Card Body */}
            <div className="p-4 sm:p-6 md:p-8">
              {loading ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-gray-200 border-t-[#2F4F4F] rounded-full animate-spin"></div>
                  <p className="text-gray-500 mt-3 sm:mt-4 text-sm sm:text-base">불러오는 중...</p>
                </div>
              ) : images.length > 0 ? (
                <div>
                  {/* 메인 슬라이더 */}
                  <div className="relative">
                    <div className="aspect-[4/3] sm:aspect-[4/3] bg-gray-100 rounded-lg sm:rounded-xl overflow-hidden">
                      <img
                        src={images[currentIndex].image_url}
                        alt={`기도제목 ${currentIndex + 1}`}
                        className="w-full h-full object-contain cursor-pointer"
                        onClick={() => openModal(currentIndex)}
                      />
                    </div>

                    {/* 네비게이션 버튼 */}
                    {images.length > 1 && (
                      <>
                        <button
                          onClick={prevSlide}
                          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all cursor-pointer"
                        >
                          <i className="ri-arrow-left-s-line text-lg sm:text-xl md:text-2xl text-gray-700"></i>
                        </button>
                        <button
                          onClick={nextSlide}
                          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all cursor-pointer"
                        >
                          <i className="ri-arrow-right-s-line text-lg sm:text-xl md:text-2xl text-gray-700"></i>
                        </button>
                      </>
                    )}

                    {/* 페이지 인디케이터 */}
                    <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm">
                      {currentIndex + 1} / {images.length}
                    </div>
                  </div>

                  {/* 썸네일 */}
                  {images.length > 1 && (
                    <div className="mt-4 sm:mt-6 flex gap-1.5 sm:gap-2 overflow-x-auto pb-2">
                      {images.map((image, index) => (
                        <button
                          key={image.id}
                          onClick={() => setCurrentIndex(index)}
                          className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-md sm:rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                            index === currentIndex
                              ? 'border-amber-500 ring-2 ring-amber-200'
                              : 'border-transparent hover:border-gray-300'
                          }`}
                        >
                          <img
                            src={image.image_url}
                            alt={`썸네일 ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* 클릭 안내 */}
                  <p className="text-center text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4">
                    <i className="ri-zoom-in-line mr-1"></i>
                    이미지를 클릭하면 크게 볼 수 있습니다
                  </p>
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 md:mb-8">
                    <i className="ri-calendar-schedule-line text-3xl sm:text-4xl md:text-5xl text-amber-500"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                    기도제목 준비 중
                  </h4>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6 sm:mb-8 max-w-md mx-auto">
                    매주 수요일, 선교 현장의 기도제목이
                    <br />
                    이곳에 업로드됩니다.
                  </p>

                  {/* Info Cards */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-6 sm:mt-8">
                    <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-5">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2F4F4F]/10 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                        <i className="ri-global-line text-base sm:text-xl text-[#2F4F4F]"></i>
                      </div>
                      <h5 className="font-semibold text-gray-900 text-xs sm:text-sm mb-0.5 sm:mb-1">
                        선교지 기도
                      </h5>
                      <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">현장의 필요를 위해</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-5">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2F4F4F]/10 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                        <i className="ri-user-heart-line text-base sm:text-xl text-[#2F4F4F]"></i>
                      </div>
                      <h5 className="font-semibold text-gray-900 text-xs sm:text-sm mb-0.5 sm:mb-1">
                        선교사 기도
                      </h5>
                      <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">사역자들을 위해</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-5">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2F4F4F]/10 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                        <i className="ri-heart-3-line text-base sm:text-xl text-[#2F4F4F]"></i>
                      </div>
                      <h5 className="font-semibold text-gray-900 text-xs sm:text-sm mb-0.5 sm:mb-1">
                        감사 기도
                      </h5>
                      <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">응답에 감사하며</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Card Footer */}
            <div className="bg-gray-50 px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-5 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <i className="ri-notification-3-line text-base sm:text-lg"></i>
                  <span className="text-xs sm:text-sm">
                    매주 수요일 기도제목이 업데이트됩니다
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[#2F4F4F]">
                  <i className="ri-time-line text-base sm:text-lg"></i>
                  <span className="text-xs sm:text-sm font-medium">수요일 밤 9:10 PM</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Quote */}
        <div className="text-center mt-8 sm:mt-12">
          <p className="text-white/70 italic text-sm sm:text-base md:text-lg">
            "두세 사람이 내 이름으로 모인 곳에는 나도 그들 중에 있느니라"
          </p>
          <p className="text-white/50 text-xs sm:text-sm mt-1 sm:mt-2">- 마태복음 18:20 -</p>
        </div>
      </div>

      {/* 이미지 모달 */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={closeModal}
        >
          <button
            onClick={closeModal}
            className="absolute top-4 sm:top-6 right-4 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl sm:text-3xl text-white"></i>
          </button>

          <div
            className="relative max-w-6xl max-h-[90vh] mx-2 sm:mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[modalIndex].image_url}
              alt={`기도제목 ${modalIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain"
            />

            {images.length > 1 && (
              <>
                <button
                  onClick={modalPrev}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-2xl sm:text-3xl text-white"></i>
                </button>
                <button
                  onClick={modalNext}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-2xl sm:text-3xl text-white"></i>
                </button>
              </>
            )}

            <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-sm sm:text-base">
              {modalIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
      `}</style>
    </section>
  );
}
